import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  LayoutDashboard, 
  Building2, 
  LogOut, 
  Plus,
  Pencil,
  Trash2,
  Search,
  Star
} from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const API_URL = '';
const LOGO_ICON = "https://customer-assets.emergentagent.com/job_qr-navigator/artifacts/rwd34j12_logo%20%284%29.png";

const FeaturedBadge = ({ isFeatured }) => {
  if (!isFeatured) return null;
  return (
    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500 text-white flex items-center gap-1">
      <Star className="w-3 h-3" />
      Featured
    </span>
  );
};

export default function AdminBusinessList() {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchBusinesses = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/businesses`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setBusinesses(data);
      }
    } catch (error) {
      console.error('Failed to fetch businesses:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBusinesses();
  }, []);

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/businesses/${deleteTarget}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Business deleted successfully');
        fetchBusinesses();
      } else {
        toast.error('Failed to delete business');
      }
    } catch (error) {
      toast.error('Failed to delete business');
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      navigate('/admin/login', { replace: true });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const filteredBusinesses = businesses.filter(biz => 
    biz.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    biz.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-business-list">
      {/* Sidebar - Desktop */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-white border-r border-slate-200 hidden md:block">
        <div className="p-6 flex items-center gap-3">
          <img 
            src={LOGO_ICON} 
            alt="Disqover" 
            className="h-8 w-8 object-contain"
          />
          <div>
            <p className="text-base font-semibold text-slate-900 font-heading">Disqover</p>
            <p className="text-xs text-slate-500 font-body">Admin Portal</p>
          </div>
        </div>
        
        <nav className="px-4 space-y-1">
          <Link
            to="/admin"
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
          >
            <LayoutDashboard className="w-5 h-5" />
            Dashboard
          </Link>
          <Link
            to="/admin/businesses"
            className="sidebar-link-active flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium"
          >
            <Building2 className="w-5 h-5" />
            Businesses
          </Link>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200">
          <Button
            onClick={handleLogout}
            variant="outline"
            size="sm"
            className="w-full"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="md:hidden bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <h1 className="text-lg font-bold text-slate-900 font-heading">Businesses</h1>
        <Button onClick={handleLogout} variant="ghost" size="sm">
          <LogOut className="w-5 h-5" />
        </Button>
      </header>

      {/* Main Content */}
      <main className="md:ml-64 p-6 pb-24 md:pb-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 font-heading">Businesses</h2>
              <p className="text-slate-500 font-body">{businesses.length} total listings</p>
            </div>
            <Link to="/admin/businesses/new">
              <Button className="rounded-full bg-teal-600 hover:bg-teal-700" data-testid="add-business-button">
                <Plus className="w-4 h-4 mr-2" />
                Add Business
              </Button>
            </Link>
          </div>

          {/* Search */}
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search businesses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12 rounded-xl"
              data-testid="search-input"
            />
          </div>

          {/* Business List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredBusinesses.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl border border-slate-100">
              <p className="text-slate-500 font-body">No businesses found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredBusinesses.map((business) => (
                <div
                  key={business.business_id}
                  className="bg-white rounded-xl border border-slate-100 p-4 flex items-center gap-4"
                  data-testid={`business-row-${business.business_id}`}
                >
                  {/* Image */}
                  <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={business.image_url || 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=200'}
                      alt={business.name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-slate-900 font-heading truncate">
                        {business.name}
                      </h3>
                      {business.is_featured && (
                        <Star className="w-4 h-4 text-amber-500 fill-amber-500 flex-shrink-0" />
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <span className="capitalize">{business.category}</span>
                      <FeaturedBadge isFeatured={business.is_featured} />
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Link to={`/admin/businesses/${business.business_id}/edit`}>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-slate-600 hover:text-slate-900"
                        data-testid={`edit-${business.business_id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                    </Link>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-slate-400 hover:text-slate-600"
                      onClick={() => setDeleteTarget(business.business_id)}
                      data-testid={`delete-${business.business_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-6 py-3 flex justify-around">
        <Link to="/admin" className="flex flex-col items-center text-slate-400">
          <LayoutDashboard className="w-6 h-6" />
          <span className="text-xs mt-1">Dashboard</span>
        </Link>
        <Link to="/admin/businesses" className="flex flex-col items-center text-teal-600">
          <Building2 className="w-6 h-6" />
          <span className="text-xs mt-1">Businesses</span>
        </Link>
      </nav>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Business</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this business? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-slate-900 hover:bg-slate-800"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
